import type { InvalidateQueryFilters } from "@tanstack/react-query";
import { generatePath, matchPath } from "react-router-dom";

import type { ContextRef } from "../protocol/protocol";
import { parseContextRef } from "../store/targets";

/** A document of a context the composer's @-mention can offer. */
export interface ContextDocument {
  /** The id in the context's own documents list (with `statement`, it names one row). */
  id: number;
  name: string;
  /** Its document type's label, as the context's documents list shows it. */
  type?: string | null;
  /** A link to open it, for the preview. */
  url?: string | null;
  /** The row is a bank statement (its id is the statement's). */
  statement?: boolean;
}

/** A query whose data gives one thing about a context (usually the page's own query, with a select). */
export interface ContextQuery<TResult, TData = any> {
  queryKey: readonly unknown[];
  queryFn: () => Promise<TData>;
  select: (data: TData) => TResult;
  staleTime?: number;
}

export type DocumentsQuery<TData = any> = ContextQuery<ContextDocument[], TData>;

/** How the web app shows one context type: the page it lives on and the queries that show it. */
export interface ContextDef {
  /** A react-router pattern with an `:id` param; a page that matches it shows this context. */
  route?: string;
  /** Everything to refetch when the assistant changed the context with this id. */
  invalidate: (id: number) => InvalidateQueryFilters[];
  /** Its documents, for @-mention in the composer. */
  documents?: (id: number) => DocumentsQuery;
  /** What it is called ("Example Coffee LLC"), for the composer and the drop area. */
  label?: (id: number) => ContextQuery<string>;
}

export type ContextDefs = Readonly<Record<string, ContextDef>>;

export const defineContexts = <T extends ContextDefs>(defs: T): T => Object.freeze(defs);

/** The context a path shows, or null for pages that show none (lists, the dashboard, /assistant). */
export function matchContext(defs: ContextDefs, pathname: string): ContextRef | null {
  for (const [type, def] of Object.entries(defs)) {
    if (!def.route) continue;
    const id = matchPath(def.route, pathname)?.params.id;
    const parsed = parseContextRef(`${type}:${id}`);
    if (parsed) return parsed.ref;
  }
  return null;
}

/** A ref's definition and id, or null for anything that is not a ref of a known type. */
export function resolveContext(
  defs: ContextDefs,
  value: string | null | undefined
): { def: ContextDef; id: number } | null {
  const parsed = parseContextRef(value);
  if (!parsed || !Object.hasOwn(defs, parsed.type)) return null;
  return { def: defs[parsed.type], id: parsed.id };
}

/** The page a ref lives on ("/applications/12"), or null for a type with no page. */
export function contextPath(defs: ContextDefs, value: string | null | undefined): string | null {
  const context = resolveContext(defs, value);
  return context?.def.route ? generatePath(context.def.route, { id: String(context.id) }) : null;
}

/**
 * The context an app link opens, and its canonical in-app path, or null. Only an absolute http(s)
 * URL on this page's origin whose path (normalised: no `..`) is a context's route counts; the path
 * is rebuilt from the ref, so no search, hash or extra segment survives. Callers check the ref is
 * the one they expect.
 */
export function appLink(defs: ContextDefs, url: string): { ref: ContextRef; path: string } | null {
  let target: URL;
  try {
    target = new URL(url);
  } catch {
    return null;
  }
  if (target.protocol !== "http:" && target.protocol !== "https:") return null;
  if (target.origin !== window.location.origin) return null;
  const ref = matchContext(defs, target.pathname);
  const path = ref && contextPath(defs, ref);
  return ref && path ? { ref, path } : null;
}

/** The queries to refetch for a ref; unknown types give none (their parent's frame comes too). */
export function invalidationsFor(defs: ContextDefs, value: string): InvalidateQueryFilters[] {
  const context = resolveContext(defs, value);
  return context ? context.def.invalidate(context.id) : [];
}
